import React, { useState, useEffect } from 'react';
import { saveVote, getVoteHistory } from '../services/voteService';

const VotingSection = ({ debateData }) => {
    const [selectedVote, setSelectedVote] = useState(null);
    const [voteHistory, setVoteHistory] = useState([]);
    const [voteStats, setVoteStats] = useState(null);
    const [loading, setLoading] = useState(false);
    const [hasVoted, setHasVoted] = useState(false);
    const [confirmVote, setConfirmVote] = useState(false);
    const [voteToConfirm, setVoteToConfirm] = useState(null);

    useEffect(() => {
        if (debateData?.topic) {
            loadVoteHistory(debateData.topic);
        }
    }, [debateData?.topic]);

    const loadVoteHistory = async (topic) => {
        try {
            setLoading(true);
            const response = await getVoteHistory(topic);
            setVoteHistory(response.voteHistory);
            setVoteStats(response.analytics[topic]);
            setLoading(false);
        } catch (error) {
            console.error('Error loading vote history:', error);
            setLoading(false);
        }
    };

    const handleVoteClick = (vote) => {
        if (hasVoted) return; 
        
        setVoteToConfirm(vote);
        setConfirmVote(true);
    };
    
    const handleConfirmVote = async () => {
        if (!debateData?.topic || !voteToConfirm) return;
        
        try {
            await saveVote(debateData.topic, voteToConfirm, null);
            setSelectedVote(voteToConfirm);
            setHasVoted(true);
            setConfirmVote(false);
            
            await loadVoteHistory(debateData.topic);
        } catch (error) {
            console.error('Error saving vote:', error);
        }
    };
    
    const handleCancelVote = () => {
        setConfirmVote(false);
        setVoteToConfirm(null);
    };

    return (
        <div className="voting-section">
            <h2>Which side was more convincing?</h2>
            {!hasVoted && !confirmVote && (
                <div className="vote-buttons">
                    <button 
                        className={`vote-button ${selectedVote === 'for' ? 'selected' : ''}`}
                        onClick={() => handleVoteClick('for')}
                        disabled={hasVoted}
                    >
                        Vote For
                    </button>
                    <button 
                        className={`vote-button ${selectedVote === 'against' ? 'selected' : ''}`}
                        onClick={() => handleVoteClick('against')}
                        disabled={hasVoted}
                    >
                        Vote Against
                    </button>
                </div>
            )}
            
            {confirmVote && (
                <div className="vote-confirmation-dialog">
                    <div className="confirmation-message">
                        <p>You are about to vote <strong>{voteToConfirm === 'for' ? 'For' : 'Against'}</strong> this topic.</p>
                        <p className="warning">This choice is final and cannot be changed.</p>
                    </div>
                    <div className="confirmation-buttons">
                        <button 
                            className="confirm-button"
                            onClick={handleConfirmVote}
                        >
                            Confirm Vote
                        </button>
                        <button 
                            className="cancel-button"
                            onClick={handleCancelVote}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}
            
            {hasVoted && (
                <div className="vote-final-confirmation">
                    <p className="final-vote-message">
                        You voted <span className={selectedVote === 'for' ? 'vote-for-text' : 'vote-against-text'}>
                            <strong>{selectedVote === 'for' ? 'For' : 'Against'}</strong>
                        </span> this topic.
                    </p>
                    <div className="vote-badge">Final Vote</div>
                </div>
            )}
            
            {voteStats && (
                <div className="vote-statistics">
                    <h3>Voting Statistics</h3>
                    
                    <div className="vote-stats-labels">
                        <span className="vote-stats-label for">For ({voteStats.for} votes)</span>
                        <span className="vote-stats-label against">Against ({voteStats.against} votes)</span>
                    </div>
                    
                    <div className="vote-chart">
                        {voteStats.for > 0 || voteStats.against > 0 ? (
                            <>
                                <div 
                                    className={`vote-bar vote-for ${voteStats.for > voteStats.against ? 'vote-winner' : ''}`}
                                    style={{ 
                                        width: `${voteStats.for > 0 ? 
                                            (voteStats.for / (voteStats.for + voteStats.against)) * 100 : 0}%` 
                                    }}
                                >
                                    {voteStats.for > 0 && (
                                        <>
                                            {Math.round((voteStats.for / (voteStats.for + voteStats.against)) * 100)}%
                                        </>
                                    )}
                                </div>
                                <div 
                                    className={`vote-bar vote-against ${voteStats.against > voteStats.for ? 'vote-winner' : ''}`}
                                    style={{ 
                                        width: `${voteStats.against > 0 ? 
                                            (voteStats.against / (voteStats.for + voteStats.against)) * 100 : 0}%` 
                                    }}
                                >
                                    {voteStats.against > 0 && (
                                        <>
                                            {Math.round((voteStats.against / (voteStats.for + voteStats.against)) * 100)}%
                                        </>
                                    )}
                                </div>
                            </>
                        ) : (
                            <div style={{ textAlign: 'center', width: '100%', padding: '10px 0' }}>
                                No votes yet
                            </div>
                        )}
                    </div>
                    
                    {voteStats.shifts > 0 && (
                        <div className="opinion-shifts">
                            <p><strong>{voteStats.shifts}</strong> users initially had a different opinion</p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default VotingSection;